import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ContentCard from '../components/ContentCard';
import DeleteButton from '../components/DeleteButton';
import { LocationTreeModal } from '../components/LocationTree';
import ImageViewer from '../components/ImageViewer';
import MergeButton from '../components/MergeButton';
import MoveBar from '../components/MoveBar';
import PageNav from '../components/PageNav';
import SearchBar from '../components/SearchBar';
import QrSection from '../components/QrSection';
import { formatCounts, locationLabel } from '../utils/labels';
import './LocationDetail.css';

function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [items, setItems] = useState([]);
  const [children, setChildren] = useState([]);
  const [ancestors, setAncestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allLocations, setAllLocations] = useState([]);
  const [moving, setMoving] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState(() => new Set());
  const [selectedItems, setSelectedItems] = useState(() => new Set());
  const [destination, setDestination] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState('');

  useEffect(() => {
    cancelMove();
    setLoading(true); // do not show the previous place while the next one loads
    fetchLocationData();
  }, [id]);

  const fetchLocationData = async () => {
    try {
      const [locRes, itemsRes, childrenRes, allRes] = await Promise.all([
        fetch(`/api/locations/${id}`),
        fetch(`/api/locations/${id}/items`),
        fetch(`/api/locations/${id}/children`),
        fetch('/api/locations/all')
      ]);
      if (locRes.status === 404) {
        // A scanned QR address carries the record's route code, not its id; resolve it
        const byRoute = await fetch(`/api/locations/route/${id}`);
        if (byRoute.ok) {
          const record = await byRoute.json();
          navigate(`/locations/${record.id}`, { replace: true });
          return;
        }
      }
      if (!locRes.ok) {
        setLocation(null);
        setLoading(false);
        return;
      }
      const locData = await locRes.json();
      setLocation(locData);
      setItems(await itemsRes.json());
      setChildren(await childrenRes.json());

      // Walk up the parent chain for the breadcrumb
      const all = await allRes.json();
      setAllLocations(all);
      const byId = new Map(all.map(loc => [loc.id, loc]));
      const chain = [];
      for (let cur = byId.get(locData.parentLocationId); cur; cur = byId.get(cur.parentLocationId)) {
        chain.unshift(cur);
      }
      setAncestors(chain);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching location data:', error);
      setLoading(false);
    }
  };

  const startMove = () => {
    setSelectedLocations(new Set());
    setSelectedItems(new Set());
    setDestination(null);
    setMoveError('');
    setMoving(true);
  };

  function cancelMove() {
    setMoving(false);
    setPickerOpen(false);
    setMoveError('');
  }

  const toggleIn = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const selectedCount = selectedLocations.size + selectedItems.size;
  const allSelected = selectedCount === children.length + items.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedLocations(new Set());
      setSelectedItems(new Set());
    } else {
      setSelectedLocations(new Set(children.map((c) => c.id)));
      setSelectedItems(new Set(items.map((i) => i.id)));
    }
  };

  const doMove = async () => {
    setMoveBusy(true);
    setMoveError('');
    try {
      const response = await fetch('/api/move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locationIds: [...selectedLocations],
          itemIds: [...selectedItems],
          destinationId: destination.id || null
        })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setMoveError(data.error || `Move failed (${response.status})`);
      } else {
        cancelMove();
        await fetchLocationData();
      }
    } catch (error) {
      setMoveError('Could not reach the server. If you were signed out, reload the page and sign in again.');
    }
    setMoveBusy(false);
  };

  if (loading) {
    return <div className="location-detail"><p>Loading...</p></div>;
  }

  if (!location) {
    return <div className="location-detail"><p>Not found</p></div>;
  }

  const label = locationLabel(location);

  return (
    <div className={`location-detail${moving ? ' moving' : ''}`}>
      <PageNav
        trail={ancestors}
        current={location.name}
        fallback={location.parentLocationId ? `/locations/${location.parentLocationId}` : '/locations'}
      />

      <SearchBar within={id} placeholder={`Search within ${location.name}`} />

      <div className="loc-detail-header">
        <div className="loc-header-content">
          {location.image && (
            <ImageViewer
              src={`data:${location.imageMimeType};base64,${location.image}`}
              alt={location.name}
              className="loc-detail-image"
            />
          )}
          <div>
            <h1>{location.name}</h1>
            {location.description && <p className="loc-description">{location.description}</p>}
          </div>
        </div>
        <div className="loc-header-actions">
          {location.parentLocationId && (
            <Link to={`/guide/locations/${id}`} className="btn btn-primary">Guide me</Link>
          )}
          <Link to={`/locations/${id}/edit`} className="btn btn-secondary">Edit</Link>
          <MergeButton
            sourceId={id}
            locations={allLocations}
            className="btn btn-secondary"
            onMerged={(targetId) => navigate(`/locations/${targetId}`)}
          />
          <DeleteButton
            url={`/api/locations/${id}`}
            noun={label.toLowerCase()}
            warning="(compartments inside it are deleted too; items inside are kept as unassigned)"
            className="btn btn-danger"
            onDeleted={() => navigate(location.parentLocationId ? `/locations/${location.parentLocationId}` : '/locations')}
          />
        </div>
      </div>

      <div className="contents">
        <div className="contents-header">
          <h2>Contents</h2>
          {!moving && (
            <div className="contents-actions">
              {(children.length > 0 || items.length > 0) && (
                <button type="button" className="btn btn-secondary" onClick={startMove}>Move</button>
              )}
              <details className="add-menu">
                <summary className="btn btn-primary">+ Add</summary>
                <div className="add-menu-options">
                  <Link to={`/locations/new?parentId=${id}`}>Add compartment</Link>
                  <Link to={`/items/new?locationId=${id}`}>Add item</Link>
                  <Link to={`/locations/${id}/quick-add`}>Add several items</Link>
                </div>
              </details>
            </div>
          )}
        </div>

        {children.length === 0 && items.length === 0 ? (
          <p className="empty-message">
            Nothing in this {label.toLowerCase()} yet. Use + Add to add a compartment or an item.
          </p>
        ) : (
          <>
            {children.length > 0 && (
              <section>
                <h3 className="contents-subheading">Compartments ({children.length})</h3>
                <div className="card-list">
                  {children.map(child => (
                    <ContentCard
                      key={child.id}
                      to={`/locations/${child.id}`}
                      image={child.image}
                      imageMimeType={child.imageMimeType}
                      placeholder="🗄️"
                      title={child.name}
                      subtitle={child.description}
                      meta={formatCounts(child)}
                      selectable={moving}
                      selected={selectedLocations.has(child.id)}
                      onToggle={() => toggleIn(setSelectedLocations, child.id)}
                    />
                  ))}
                </div>
              </section>
            )}
            {items.length > 0 && (
              <section>
                <h3 className="contents-subheading">Items ({items.length})</h3>
                <div className="card-list">
                  {items.map(item => (
                    <ContentCard
                      key={item.id}
                      to={`/items/${item.id}`}
                      image={item.image}
                      imageMimeType={item.imageMimeType}
                      placeholder="📦"
                      title={item.name}
                      subtitle={item.description}
                      meta={item.count > 1 ? `Count: ${item.count}` : null}
                      selectable={moving}
                      selected={selectedItems.has(item.id)}
                      onToggle={() => toggleIn(setSelectedItems, item.id)}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <QrSection qrCode={location.qrCode} name={location.name} noun={label.toLowerCase()} />
      <Link to={`/locations/${id}/share`} className="share-link-row">
        Share this {label.toLowerCase()} with guests
        <span aria-hidden="true">›</span>
      </Link>

      {moving && (
        <MoveBar
          count={selectedCount}
          allSelected={allSelected}
          onToggleAll={toggleAll}
          destinationLabel={destination ? destination.path : null}
          onChooseDestination={() => setPickerOpen(true)}
          onMove={doMove}
          onCancel={cancelMove}
          busy={moveBusy}
          error={moveError}
        />
      )}
      {pickerOpen && (
        <LocationTreeModal
          title="Move to..."
          topLabel="Top level (no parent location)"
          locations={allLocations}
          excludeIds={[...selectedLocations]}
          selectedId={destination ? destination.id : ''}
          onSelect={(destId, path) => {
            setDestination({ id: destId, path });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default LocationDetail;
